import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Table, Spin, message } from "antd";
import { usePensionTransactions } from "../../hooks/products/pension/usePensionTransactions";
import { api } from '../../lib/api-client';
// --- START: CustomPagination Component Definition (Required for UI) ---
function CustomPagination({ current, pageSize, total, onChange }) {
  const pageCount = Math.ceil(total / pageSize);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const navBtnStyle = {
    background: 'transparent',
    color: '#184b18',
    border: 'none',
    borderRadius: 0,
    padding: '0 10px',
    minWidth: 0,
    height: 26,
    fontWeight: 700,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    boxShadow: 'none',
    margin: '0 2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  };
  const navBtnDisabledStyle = {
    ...navBtnStyle,
    color: '#bdbdbd',
    cursor: 'not-allowed',
    opacity: 0.6,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        style={current === 1 ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Previous page"
      >
        {'<'}
      </button>
      {pages.map(page => (
        <button
          key={page}
          onClick={() => onChange(page)}
          style={{
            border: '0.5px solid #184b18',
            background: page === current ? 'linear-gradient(90deg, #05b005d7 0%, #00b050 60%, #90ee90 100%)' : 'transparent',
            color: page === current ? '#fff' : '#184b18',
            borderRadius: 18,
            padding: '0 14px',
            minWidth: 36,
            height: 36,
            fontWeight: 700,
            fontSize: 12,
            outline: 'none',
            cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
            boxShadow: 'none',
            margin: '0 2px',
          }}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => current < pageCount && onChange(current + 1)}
        disabled={current === pageCount}
        style={current === pageCount ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Next page"
      >
        {'>'}
      </button>
    </div>
  );
}
// --- END: CustomPagination Component Definition ---


export default function PensionTransactionsHistory({ transactions: propTransactions = [], pagination: propPagination = {} }) {
  const location = useLocation();
  const policyNumber = location.state?.productDetails?.policyNumber;
  const [filterYear, setFilterYear] = useState(null);
  const [filterMonth, setFilterMonth] = useState(null);
  const { data: transactionsData, isLoading: transactionsLoading } = usePensionTransactions(
    { policyNumber, ...(filterYear ? { year: filterYear } : {}), ...(filterMonth ? { month: filterMonth } : {}) },
    !!policyNumber && !!filterYear
  );

  const [availableYears, setAvailableYears] = useState([]);
  const [loadingYears, setLoadingYears] = useState(false);

  React.useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(filterYear)) {
      setFilterYear(availableYears[0]);
    }
  }, [availableYears, filterYear]);

  const transactions = transactionsData?.data?.transaction_history?.transactions || propTransactions;
  const pagination = transactionsData?.data?.transaction_history?.pagination || propPagination;
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = pagination.page_size || 10;
  const [isStatementGenerating, setIsStatementGenerating] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Fetch available fund years from API
  React.useEffect(() => {
    const fetchYears = async () => {
      setLoadingYears(true);
      try {
        const response = await api.get('/contributed/years');
        const years = response?.data?.data || [];
        const sorted = years
          .map(y => y.fund_year)
          .filter(Boolean)
          .sort((a, b) => b - a);
        setAvailableYears(sorted);
      } catch (err) {
        console.error('Failed to fetch contribution years:', err);
      } finally {
        setLoadingYears(false);
      }
    };
    fetchYears();
  }, []);
  const searchInputRef = React.useRef(null);

  // Normalize transactions for table
  const normalizedTransactions = transactions.map(item => ({
    date: item.payment_date || "",
    pension_type_name: item.pension_type_name || "",
    scheme_name: item.scheme_name || "",
    amount: item.received || 0,
    units_allocated: item.units_allocated || 0,
    unit_price: item.unit_price || 0,
    narration: item.narration || "",
    employer_contribution: item.employer_contribution || 0
  }));

  // Sort by date descending
  const sortedTransactions = [...normalizedTransactions].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Table columns - Enhanced readability for mobile via `whiteSpace: 'nowrap'`
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      render: date => <div style={{ textAlign: "left", paddingLeft: 8, whiteSpace: 'nowrap' }}>{formatDate(date)}</div>
    },
    {
      title: "Type",
      dataIndex: "pension_type_name",
      key: "pension_type_name",
      align: "left"
    },
    {
      title: "Scheme",
      dataIndex: "scheme_name",
      key: "scheme_name",
      align: "left"
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      render: amount => <span style={{ color: "#111", fontWeight: 600, whiteSpace: 'nowrap' }}>GHS {Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    },
    {
      title: "Units",
      dataIndex: "units_allocated",
      key: "units_allocated",
      align: "left",
      responsive: ['md'] // Hide on small screens to save space, but keep in scroll
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      align: "left",
      render: (v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      responsive: ['md'] // Hide on small screens to save space, but keep in scroll
    },
    {
      title: "Employer Contrib.",
      dataIndex: "employer_contribution",
      key: "employer_contribution",
      align: "left",
      render: contrib => <span style={{ whiteSpace: 'nowrap' }}>GHS {Math.abs(contrib).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>,
      responsive: ['sm'] // Hide on extra small screens, keep on small to large
    },
  ];

  // Filtering logic
  let filteredTransactions = sortedTransactions;
  const isSearchActive = !!searchValue;
  if (isSearchActive) {
    const val = searchValue.trim().toLowerCase();
    filteredTransactions = filteredTransactions.filter(t =>
      t.amount.toString().includes(val) ||
      t.narration.toLowerCase().includes(val) ||
      t.pension_type_name.toLowerCase().includes(val)
    );
  }

  // Pagination (omitted for brevity)
  const paginatedTransactions = React.useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(startIdx, startIdx + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  // Helpers
  function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    // Use an abbreviated format for mobile
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  }
  async function handleStatementDownload() {
    setIsStatementGenerating(true);
    try {
      const response = await api.get('/pensions/statement');
      const downloadUrl = response?.data?.data?.download_url;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      } else {
        message.error('Statement generation failed. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate statement', err);
      message.error('Failed to generate statement. Please try again.');
    } finally {
      setIsStatementGenerating(false);
    }
  }
  
  function handleSearchClick() {
    setShowSearch(prev => !prev);
    setTimeout(() => {
      if (!showSearch && searchInputRef.current) searchInputRef.current.focus();
    }, 200);
  }

  // UI
  return (
    <div>
      <div className="container p-0 transactions-page">
       
         <div className="py-4 d-flex flex-column align-items-center">
           {/* HEADER/FILTERS ROW - Aligned with table width */}
           <div className="mb-3 w-100" style={{ maxWidth: 1330, paddingLeft: 12, paddingRight: 12 }}>
             <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 16 }}>
               <div className="mb-2 mb-sm-0">
                 <div className="page-title">Pension Transactions</div>
               </div>
               
               <div className="d-flex flex-wrap align-items-center controls-container" style={{ gap: 12 }}>
                 {/* Search Input */}
                 <div className="search-container" style={{ position: 'relative', display: 'flex', alignItems: 'center', borderRadius: 24, background: '#fff', boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)', padding: '4px 4px 4px 12px', transition: 'all .28s ease', width: showSearch ? 280 : 120, minWidth: showSearch ? 120 : 120 }}>
                   <i
                     className="bi bi-search"
                     style={{ fontSize: 18, color: '#666', cursor: 'pointer', zIndex: 2, marginRight: 8, flexShrink: 0 }}
                     aria-label="Search"
                     onClick={handleSearchClick}
                     tabIndex={0}
                     onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSearchClick(); }}
                     role="button"
                   ></i>
                   <input
                     ref={searchInputRef}
                     type="text"
                     placeholder={showSearch ? "Search by type/amount/narration..." : "Search"}
                     className="form-control border-0"
                     style={{ background: 'transparent', border: 'none', boxShadow: 'none', textAlign: 'left', padding: '8px 0', width: showSearch ? 200 : 60, opacity: 1, transition: 'width .28s ease' }}
                     value={searchValue}
                     onChange={e => setSearchValue(e.target.value)}
                    onBlur={() => { if (!searchValue) setShowSearch(false); }} 
                   />
                 </div>
                 
                 {/* Fund Year Filter */}
                 <div className="controls-left d-flex align-items-center" style={{ gap: 12 }}>
                   <omk-select
                     label="Fund Year"
                     value={filterYear || ''}
                     disabled={!policyNumber || loadingYears || availableYears.length === 0}
                     onInput={(e) => setFilterYear(Number(e.target.value))}
                   >
                     {loadingYears ? (
                       <omk-select-option value="">Loading...</omk-select-option>
                     ) : availableYears.length > 0 ? (
                       availableYears.map(y => (
                         <omk-select-option key={y} value={y}>{y}</omk-select-option>
                       ))
                     ) : (
                       <omk-select-option value="">No years available</omk-select-option>
                     )}
                   </omk-select>

                   <omk-select
                     label="Month"
                     value={filterMonth || ''}
                     disabled={!policyNumber}
                     onInput={(e) => setFilterMonth(e.target.value ? Number(e.target.value) : null)}
                   >
                     <omk-select-option value="1">January</omk-select-option>
                     <omk-select-option value="2">February</omk-select-option>
                     <omk-select-option value="3">March</omk-select-option>
                     <omk-select-option value="4">April</omk-select-option>
                     <omk-select-option value="5">May</omk-select-option>
                     <omk-select-option value="6">June</omk-select-option>
                     <omk-select-option value="7">July</omk-select-option>
                     <omk-select-option value="8">August</omk-select-option>
                     <omk-select-option value="9">September</omk-select-option>
                     <omk-select-option value="10">October</omk-select-option>
                     <omk-select-option value="11">November</omk-select-option>
                     <omk-select-option value="12">December</omk-select-option>
                   </omk-select>
                 </div>

                 {/* Generate Statement Button */}
                 <div className="controls-right d-flex align-items-center">
                   <button className="btn btn-primary generate-btn" onClick={handleStatementDownload} disabled={isStatementGenerating}>{isStatementGenerating ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Generating...</span> : 'Generate Statement'}</button>
                 </div>
               </div>
             </div>
           </div>
          
          
          {/* Component-level responsive styles - ENHANCED */}
          <style>{`
            .transactions-page .page-title { font-weight:700; color:#111; font-size: clamp(18px, 2.2vw, 22px); }
            .transactions-page .search-container { border-radius: 24px; background: #fff; box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08); overflow: hidden; }
            .transactions-page .search-field { border-radius: 24px; padding: 8px 12px; height: 36px; }
            .transactions-page .generate-btn { border-radius: 24px; height: 36px; font-weight: 600; min-width: 120px; }
            .transactions-page .card { width: 97%; max-width: 1330px; min-width: 0; }
            .transactions-page .ant-table-wrapper { overflow-x: auto; }
            .transactions-page .ant-table-body { overflow-x: auto !important; }
            .transactions-page .ant-table tbody td { font-size: clamp(12px, 1.8vw, 14px); }
            .transactions-page .ant-table thead th { font-size: clamp(12px, 1.8vw, 13px); }
            .transactions-page .mobile-full-width { width: 80%; max-width: 420px; }
            
            /* Tablet/Mid-Screen Optimization (900px) */
            @media (max-width: 900px) {
              .transactions-page .generate-btn { min-width: 96px; padding: 8px 12px; }
              .transactions-page .search-container { width: 160px !important; min-width: 160px !important; }
            }

            /* Mobile-First Optimization (Small Screens) */
            @media (max-width: 576px) {
              .transactions-page { padding: 8px; }
              .transactions-page .page-title { font-size: 16px; margin-bottom: 12px; }
              
              .transactions-page .controls-container { 
                width: 100%; 
                flex-direction: column;
                align-items: stretch;
                gap: 12px;
              }
              .transactions-page .controls-left { 
                width: 100%;
                flex-direction: column;
                align-items: stretch;
                gap: 12px;
              }
              .transactions-page .controls-left > div {
                width: 100%;
              }
              .transactions-page .controls-left omk-select {
                width: 100% !important;
                min-width: 100% !important;
              }
              .transactions-page .controls-right { 
                width: 100%;
              }
              .transactions-page .controls-right .generate-btn {
                width: 100%;
              }

              .transactions-page .search-container { 
                width: 100% !important;
                min-width: 100% !important;
                padding: 8px 12px 8px 12px !important;
              }
              .transactions-page .search-container input {
                width: 100% !important;
              }

              .transactions-page .generate-btn { 
                min-width: unset; 
                padding: 10px 16px; 
                font-size: 13px; 
                height: 40px;
              }

              .transactions-page .card { width: 100%; min-width: 0; border-radius: 10px; }
              .transactions-page .ant-table { font-size: 11px; }
              .transactions-page .ant-table table { min-width: max-content; }
              .transactions-page .ant-table thead th,
              .transactions-page .ant-table tbody td {
                padding: 8px 4px;
                font-size: 11px;
              }
              
              .transactions-page .mobile-full-width { width: 100% !important; max-width: 100%; }

              .transactions-page .d-flex.justify-content-between.align-items-center.px-4.py-3 {
                flex-direction: column;
                gap: 12px;
                padding: 12px;
              }
            }
            
            /* Extra Small Screens */
            @media (max-width: 480px) {
              .transactions-page .page-title { font-size: 15px; }
              .transactions-page .search-container { padding: 6px 10px 6px 10px !important; }
              .transactions-page .ant-table { font-size: 10px; }
              .transactions-page .ant-table thead th,
              .transactions-page .ant-table tbody td {
                padding: 6px 2px;
                font-size: 10px;
              }
            }
          `}</style>
          
          {/* Table Container */}
          <div className="w-100 d-flex justify-content-center" style={{ minHeight: 120, background: 'transparent' }}>
              <div className="card p-0 shadow-sm" style={{ width: '97%', maxWidth: 1330, minWidth: 320, color: '#111', overflow: 'auto', borderRadius: 10, position: 'relative' }}>
                {transactionsLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 20 }}>
                    <Spin size="large" />
                  </div>
                )}
                <Table
                  columns={columns}
                  dataSource={paginatedTransactions}
                  rowKey={(record, idx) => idx}
                  pagination={false}
                  locale={{ emptyText: <span className="text-muted" style={{ fontSize: 18 }}>No transactions found.</span> }}
                  style={{ overflow: 'hidden', borderRadius: 24 }}
                  scroll={{ x: 'max-content' }}
                />
              </div>
          </div>
          
          {/* Pagination */}
          <div className="w-100 d-flex justify-content-center mt-2">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ maxWidth: 1330, minWidth: 0, width: '100%', borderRadius: 10, border: 'none', background: 'transparent' }}>
              <div className="text-muted d-none d-sm-block" style={{ fontSize: 15, fontWeight: 500 }}>
                Showing 1-{paginatedTransactions.length} of {filteredTransactions.length} results
              </div>
              <CustomPagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredTransactions.length}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}